/**
 * Storezone — Admin Layout
 *
 * Sidebar + content shell for Admin (tenant owner/manager) view.
 * Uses the 'tenant' theme if branding is loaded, 'storezone' otherwise.
 *
 * Structure:
 *   Sidebar (240px): [Logo] [Nav] [User]
 *   Main: [Topbar: breadcrumb + actions] [Page content]
 *
 * navItems are passed from route-level configuration so this layout
 * is reusable across all admin feature modules.
 */

import { type ReactNode, useState, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { Icons } from '@/lib/icons'
import { BranchSelector } from '@/components/BranchSelector'
import { LoadingPage } from '@/components/states/LoadingPage'
import type { ModuleKey } from '@/types'
import './AdminLayout.css'

export interface AdminNavItem {
  id: string
  label: string
  to: string
  Icon: React.ComponentType<{ size?: number }>
  badge?: string | number
  requiredModule?: ModuleKey
}

export interface AdminNavGroup {
  group?: string
  items: AdminNavItem[]
}

interface AdminLayoutProps {
  children: ReactNode
  navGroups?: AdminNavGroup[]
}

const DEFAULT_NAV_GROUPS: AdminNavGroup[] = [
  {
    items: [
      { id: 'dashboard',  label: 'Dashboard',  to: '/admin',           Icon: Icons.Dashboard  },
    ],
  },
  {
    group: 'Catalog',
    items: [
      { id: 'products',   label: 'Products',   to: '/admin/products',  Icon: Icons.Products   },
      { id: 'catalog_setup', label: 'Catalog Setup', to: '/admin/catalog/setup', Icon: Icons.Settings },
      { id: 'inventory',  label: 'Inventory',  to: '/admin/inventory', Icon: Icons.Inventory  },
    ],
  },
  {
    group: 'Store',
    items: [
      { id: 'orders',     label: 'Orders',     to: '/admin/orders',    Icon: Icons.Orders     },
      { id: 'customers',  label: 'Customers',  to: '/admin/customers', Icon: Icons.Customers  },
      { id: 'payments',   label: 'Payments',   to: '/admin/payments/collections',  Icon: Icons.Payments   },
    ],
  },
  {
    group: 'Operations',
    items: [
      { id: 'suppliers',  label: 'Suppliers',  to: '/admin/purchasing/suppliers', Icon: Icons.Users, requiredModule: 'purchasing' },
      { id: 'purchasing', label: 'Purchasing', to: '/admin/purchasing/orders', Icon: Icons.Purchasing, requiredModule: 'purchasing' },
      { id: 'transfers',  label: 'Transfers',  to: '/admin/transfers', Icon: Icons.Transfer   },
      { id: 'returns',    label: 'Returns',    to: '/admin/returns',   Icon: Icons.Return     },
      { id: 'expenses',   label: 'Expenses',   to: '/admin/expenses',  Icon: Icons.Expense    },
    ],
  },
  {
    group: 'Management',
    items: [
      { id: 'sellers',    label: 'Staff',      to: '/admin/staff',     Icon: Icons.Users      },
      { id: 'branches',   label: 'Branches',   to: '/admin/branches',  Icon: Icons.Branches, requiredModule: 'branching' },
      { id: 'reports',    label: 'Reports',    to: '/admin/reports',   Icon: Icons.Reports    },
      { id: 'settings',   label: 'Settings',   to: '/admin/settings',  Icon: Icons.Settings   },
    ],
  },
]

export function AdminLayout({ children, navGroups = DEFAULT_NAV_GROUPS }: AdminLayoutProps) {
  const { profile, signOut } = useAuth()
  const { activeMembership, branding, hasModule, ready } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  // Auto-expand the active group when location changes
  useEffect(() => {
    if (collapsed) return
    const newExpanded = { ...expandedGroups }
    let changed = false
    navGroups.forEach(g => {
      if (g.group) {
        const isActive = g.items.some(item => location.pathname === item.to || (item.to !== '/admin' && location.pathname.startsWith(item.to)))
        if (isActive && !newExpanded[g.group]) {
          newExpanded[g.group] = true
          changed = true
        }
      }
    })
    if (changed) setExpandedGroups(newExpanded)
  }, [location.pathname, navGroups, collapsed])

  const toggleGroup = (groupName: string) => {
    if (collapsed) setCollapsed(false)
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }))
  }

  if (!ready) {
    return <LoadingPage message="Loading tenant context..." />
  }

  async function handleSignOut(e?: any) {
    if (e) e.preventDefault()
    await signOut()
    navigate('/auth/login', { replace: true })
  }

  const themeStyles = {
    '--sz-primary': branding?.primary_color || undefined,
    '--sz-secondary': branding?.secondary_color || undefined,
    '--sz-accent': branding?.accent_color || undefined,
    '--sz-sidebar': branding?.sidebar_color || undefined,
    '--sz-radius': branding?.button_radius ? `${branding.button_radius}px` : undefined,
  } as React.CSSProperties

  return (
    <div className={`sz-admin ${collapsed ? 'sz-admin--collapsed' : ''}`} style={themeStyles}>

      {/* Sidebar */}
      <aside className="sz-admin__sidebar" aria-label="Sidebar navigation">

        {/* Sidebar Header */}
        <div className="sz-admin__sidebar-header">
          <NavLink to="/admin" className="sz-admin__brand">
            {branding?.logo_url ? (
              <img src={branding.logo_url as string} alt="Logo" className="sz-admin__brand-mark" style={{ objectFit: 'contain', background: 'transparent' }} />
            ) : (
              <span className="sz-admin__brand-mark">SZ</span>
            )}
            {!collapsed && (
              <span className="sz-admin__brand-name">
                {activeMembership?.tenant_name ?? 'Storezone'}
              </span>
            )}
          </NavLink>
          <button
            className="sz-admin__collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icons.Menu size={16} />
          </button>
        </div>

        {/* Branch Context */}
        {!collapsed && (
          <div style={{ padding: '0 var(--sz-space-4) var(--sz-space-4)' }}>
            <BranchSelector />
          </div>
        )}

        {/* Navigation */}
        <nav className="sz-admin__nav">
          {navGroups.map((group, gi) => {
            const isGroupExpanded = group.group ? expandedGroups[group.group] : true
            
            return (
              <div key={gi} className="sz-admin__nav-group">
                {group.group && (
                  <button 
                    type="button"
                    className={`sz-admin__nav-group-header ${collapsed ? 'sz-admin__nav-group-header--collapsed' : ''}`}
                    onClick={() => toggleGroup(group.group!)}
                    title={collapsed ? group.group : undefined}
                  >
                    {!collapsed && <span className="sz-admin__nav-group-label">{group.group}</span>}
                    {collapsed && <span className="sz-admin__nav-group-label-short">{group.group.substring(0, 3)}</span>}
                    {!collapsed && (
                      <span className={`sz-admin__nav-group-chevron ${isGroupExpanded ? 'sz-admin__nav-group-chevron--open' : ''}`}>
                        <Icons.ChevronDown size={14} />
                      </span>
                    )}
                  </button>
                )}
                
                <div className={`sz-admin__nav-items ${(!isGroupExpanded && !collapsed) ? 'sz-admin__nav-items--hidden' : ''}`}>
                  {group.items.map(({ id, label, to, Icon, badge, requiredModule }) => {
                    if (requiredModule && !hasModule(requiredModule)) return null;
                    return (
                      <NavLink
                        key={id}
                      to={to}
                      end={to === '/admin'}
                      className={({ isActive }) =>
                        `sz-admin__nav-link${isActive ? ' sz-admin__nav-link--active' : ''}`
                      }
                      title={collapsed ? label : undefined}
                    >
                      <span className="sz-admin__nav-icon"><Icon size={16} /></span>
                      {!collapsed && (
                        <>
                          <span className="sz-admin__nav-label">{label}</span>
                          {badge !== undefined && (
                            <span className="sz-admin__nav-badge">{badge}</span>
                          )}
                        </>
                      )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            )
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="sz-admin__sidebar-footer">
          {!collapsed && (
            <div className="sz-admin__user-info">
              <span className="sz-admin__user-name truncate">
                {profile?.full_name ?? profile?.email ?? '—'}
              </span>
              <span className="sz-admin__user-role truncate">
                {activeMembership?.role_name ?? ''}
              </span>
            </div>
          )}
          <button
            type="button"
            id="admin-signout"
            className="sz-admin__signout"
            onClick={handleSignOut}
            title="Sign out"
            aria-label="Sign out"
          >
            <Icons.LogOut size={15} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="sz-admin__main">
        {children}
      </main>
    </div>
  )
}
